import type { Product } from '@grafikr/shopify-typescript/type/json/Product';
import isGraphqlId from 'utils/shopify/is-graphql-id';
import getGraphqlId from 'utils/shopify/get-graphql-id';
import storefront from 'utils/shopify/storefront';
import { htmlUnescape } from 'escape-goat';

const PRODUCT_NODES_HANDLE_QUERY = `#graphql 
  query ProductsHandlesQuery($ids: [ID!]!) {
    nodes(ids: $ids) {
      ... on Product {
        handle
      }
    }
  }
`;

window._learnq = window._learnq || [];
window._learnq.push([
  'track',
  'Viewed Page',
  {
    email: theme.customer?.email,
    PageName: htmlUnescape(theme.strings.general.currentPageTitle),
    url: window.location.href,
  },
]);

document.addEventListener('wishlist:updated', async (event) => {
  const { added: addedId, list } = event.detail;

  if (!addedId || !list) {
    return;
  }

  window._learnq = window._learnq || [];
  window.dataLayer = window.dataLayer || [];

  const nodeIds = list.filter((item) => isGraphqlId(item));
  const handles = list.filter((item) => !isGraphqlId(item));

  if (nodeIds.length > 0) {
    const { data } = await storefront.request(PRODUCT_NODES_HANDLE_QUERY, {
      variables: { ids: nodeIds },
    });

    if (data) {
      handles.push(
        ...data.nodes
          .filter((node): node is { handle: string } => node !== null)
          .map((node) => node.handle),
      );
    }
  }

  const promises = handles.map((handle) =>
    fetch(`${theme.routes.root}products/${handle}.json`).then((response) => {
      if (response.ok) {
        return response.json();
      }

      return null;
    }),
  );

  const products = (await Promise.all<{ product: Product } | null>(promises))
    .filter((promise): promise is { product: Product } => promise !== null)
    .map((promise) => promise.product);

  const addedProduct = products.find((product) => {
    const isGraphql = isGraphqlId(addedId);

    if (isGraphql) {
      return getGraphqlId(product.id, 'Product') === addedId;
    }

    return product.handle === addedId;
  });

  if (addedProduct) {
    const firstVariant = addedProduct.variants.at(0);

    if (!firstVariant) {
      throw new Error('Added product does not have variants');
    }

    window._learnq.push([
      'track',
      'Add to Wishlist',
      {
        $value: products
          .reduce((total, product) => total + parseFloat(product.variants[0]?.price ?? '0'), 0)
          .toFixed(2),
        AddedItemProductName: addedProduct.title,
        AddedItemProductID: addedProduct.id.toString(),
        AddedItemCategory: addedProduct.product_type.split('_').at(0),
        AddedItemSubcategory: addedProduct.product_type.split('_').at(1),
        Items: products.map((product) => ({
          ProductId: product.id.toString(),
          SKU: firstVariant.sku,
          ProductName: product.title,
          Quantity: 1,
          ItemPrice: firstVariant.price,
          RowTotal: firstVariant.price,
          ProductURL: `${theme.routes.root}products/${product.handle}`,
          ImageURL: product.image.src,
          Category: product.product_type.split('_').at(0),
          Subcategory: product.product_type.split('_').at(1),
          Vendor: product.vendor,
        })),
      },
    ]);

    const [category, category2] = addedProduct.product_type.split('_');

    const gtmEvent = {
      currency: Shopify.currency.active,
      value: parseFloat(firstVariant.price),
      items: [
        {
          item_id: firstVariant.sku,
          item_sku: firstVariant.sku,
          item_name: addedProduct.title,
          item_brand: addedProduct.vendor,
          item_category: category,
          item_category2: category2,
          price: parseFloat(firstVariant.price),
          quantity: 1,
        },
      ],
    };

    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: 'add_to_wishlist',
      ecommerce: gtmEvent,
    });
    Shopify.analytics.publish('add_to_wishlist', gtmEvent);
  }
});

window.addEventListener('klaviyoForms', ({ detail }) => {
  if (detail.type !== 'submit') {
    return;
  }

  window._learnq = window._learnq || [];
  window._learnq.push(['track', 'Submitted form', { formId: detail.formId, ...detail.metaData }]);
});
